/**
 * Enum values mirrored from the database. Keep in sync with
 * `supabase/migrations/0002_enums.sql` — the pgTAP suite asserts the DB side,
 * and `assertNever` keeps the TypeScript side exhaustive.
 */

export const ACCOUNT_TYPES = ['broker', 'bank', 'manual'] as const;
export type AccountType = (typeof ACCOUNT_TYPES)[number];

export const ASSET_CLASSES = ['equity', 'bond', 'cash', 'commodity', 'crypto', 'other'] as const;
export type AssetClass = (typeof ASSET_CLASSES)[number];

export const PRICE_SOURCES = ['eodhd', 'ibkr_mark', 'manual'] as const;
export type PriceSource = (typeof PRICE_SOURCES)[number];

export const INVESTMENT_TRANSACTION_TYPES = [
  'buy',
  'sell',
  'dividend',
  'fee',
  'tax',
  'interest',
] as const;
export type InvestmentTransactionType = (typeof INVESTMENT_TRANSACTION_TYPES)[number];

export const CATEGORY_BUCKETS = ['spendable', 'emergency', 'long_term'] as const;
export type CategoryBucket = (typeof CATEGORY_BUCKETS)[number];

export const MATCH_FIELDS = ['description', 'counterparty'] as const;
export type MatchField = (typeof MATCH_FIELDS)[number];

export const INTEGRATION_STATUSES = [
  'connected',
  'expiring',
  'expired',
  'error',
  'disabled',
] as const;
export type IntegrationStatus = (typeof INTEGRATION_STATUSES)[number];

export const SYNC_STATUSES = ['running', 'success', 'partial', 'error'] as const;
export type SyncStatus = (typeof SYNC_STATUSES)[number];

/** Display + storage currencies supported in v1. */
export const CURRENCIES = ['HUF', 'EUR', 'USD', 'THB'] as const;
export type Currency = (typeof CURRENCIES)[number];

/** The base currency of `fx_rates_daily` — ECB quotes everything against EUR. */
export const FX_BASE_CURRENCY = 'EUR' satisfies Currency;

/** Default display currency until the user picks another one. */
export const DEFAULT_DISPLAY_CURRENCY = 'HUF' satisfies Currency;

export function isCurrency(value: string): value is Currency {
  return (CURRENCIES as readonly string[]).includes(value);
}

/** Compile-time exhaustiveness guard for switch statements over these unions. */
export function assertNever(value: never, message = 'Unexpected value'): never {
  throw new Error(`${message}: ${JSON.stringify(value)}`);
}
